import axios from 'axios';

export type NotificationCategory = 'project' | 'comment' | 'lead' | 'system';
export type NotificationSeverity = 'info' | 'success' | 'warning' | 'error';

export interface NotificationRecord {
  id: number;
  event_key?: string | null;
  type: string;
  category: NotificationCategory | string;
  severity: NotificationSeverity | string;
  fk_actor_user?: number | null;
  entity_type?: string | null;
  entity_id?: number | null;
  title?: string | null;
  body?: string | null;
  meta?: any;
  createdAt?: string;
}

export interface NotificationRecipientItem {
  id: number;
  read_at?: string | null;
  notification: NotificationRecord | null;
}

export interface NotificationPreference {
  category: NotificationCategory | string;
  in_app: boolean;
  email: boolean;
}

const API = axios.create({
  baseURL: process.env.UMI_APP_API_URL || 'http://localhost:1337',
  headers: {
    'Content-Type': 'application/json',
  },
});

API.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('token') || '';
    if (config && config.headers) {
      (config.headers as any).Authorization = `Bearer ${token}`;
    }
  } catch (e) {}
  return config;
});

function unwrapArray(data: any): any[] {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

function unwrapObject<T = any>(data: any): T | null {
  if (data && data.data !== undefined) return data.data as T;
  if (data) return data as T;
  return null;
}

class NotificationService {
  async fetchNotifications(params: {
    unreadOnly?: boolean;
    category?: string;
    type?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<NotificationRecipientItem[]> {
    try {
      const { data } = await API.get('/notification', { params });
      return unwrapArray(data) as NotificationRecipientItem[];
    } catch (e) {
      return [];
    }
  }

  async getUnreadCount(): Promise<number> {
    try {
      const { data } = await API.get('/notification/unread-count');
      const payload = unwrapObject<{ unread: number }>(data);
      return payload && typeof payload.unread === 'number' ? payload.unread : 0;
    } catch (e) {
      return 0;
    }
  }

  async markRead(recipientId: number): Promise<boolean> {
    try {
      await API.patch(`/notification/${recipientId}/mark-read`, {});
      return true;
    } catch (e) {
      return false;
    }
  }

  async markAllRead(): Promise<boolean> {
    try {
      await API.post('/notification/mark-all-read', {});
      return true;
    } catch (e) {
      return false;
    }
  }

  async getPreferences(): Promise<NotificationPreference[]> {
    try {
      const { data } = await API.get('/notification/preferences');
      return unwrapArray(data) as NotificationPreference[];
    } catch (e) {
      return [];
    }
  }

  async updatePreferences(preferences: NotificationPreference[]): Promise<NotificationPreference[]> {
    try {
      const { data } = await API.patch('/notification/preferences', { preferences });
      return unwrapArray(data) as NotificationPreference[];
    } catch (e) {
      return [];
    }
  }
}

export default new NotificationService();
